import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import type { User } from '../lib/types';
import { fetchMe, logout as apiLogout } from '../lib/api';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  error: null,
  logout: async () => {},
  refresh: async () => {},
});

export function useAuthProvider(): AuthContextValue {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMe();
      console.log('[auth] fetchMe result:', data);
      setUser(data.user);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch user';
      console.error('[auth] fetchMe error:', msg, err);
      setError(msg);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
      window.location.href = '/';
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { user, loading, error, logout, refresh };
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
